import { pb } from "./pb";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${pb.authStore.token}`,
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status} ${detail}`);
  }
  return (await res.json()) as T;
}

// ─── GSC (El Portero) ─────────────────────────────────────────────────
export interface GscStatus {
  connected: boolean;
  connected_at: string | null;
}

export interface GscSite {
  siteUrl: string;
  permissionLevel: string;
}

export interface GscKeywordRow {
  query: string;
  position: number;
  impressions: number;
  clicks: number;
  ctr: number;
  striking_distance: boolean;
}

export interface GscKeywordsResponse {
  site: string;
  months: number;
  count: number;
  rows: GscKeywordRow[];
  fetched_at: string;
  cached: boolean;
}

export interface GscGeneratePayload {
  query: string;
  site?: string;
  position?: number;
  impressions?: number;
}

// ─── Analytics (GSC snapshot) ─────────────────────────────────────────
export interface AnalyticsSummary {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface AnalyticsDailyRow {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface AnalyticsQueryRow {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface AnalyticsPageRow {
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface AnalyticsCannibalization {
  query: string;
  pages: string[];
  positions: number[];
}

export interface AnalyticsDeltas {
  prev: AnalyticsSummary;
  current: AnalyticsSummary;
  delta: {
    clicks: number;
    impressions: number;
    ctr_pp: number;
    position: number;
  };
  window_days: number;
}

export interface AnalyticsResponse {
  generated_at: string;
  period: string;
  site_url: string;
  summary: AnalyticsSummary;
  daily: AnalyticsDailyRow[];
  top_queries: AnalyticsQueryRow[];
  top_pages: AnalyticsPageRow[];
  cannibalization: AnalyticsCannibalization[];
  deltas: AnalyticsDeltas;
  cached_at?: number;
  stale?: boolean;
}

// ─── Blog (published posts + perf) ────────────────────────────────────
export interface BlogPostPerf {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface BlogPost {
  slug: string;
  category: string;
  keyword: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  published_at: string;
  reading_time: number | null;
  url: string;
  perf: BlogPostPerf | null;
}

export interface BlogSummary {
  total_posts: number;
  posts_with_perf: number;
  total_clicks: number;
  total_impressions: number;
  analytics_period: string | null;
  by_category: Record<string, number>;
}

export interface BlogResponse {
  posts: BlogPost[];
  summary: BlogSummary;
  cached_at: number;
}

// ─── Reports (weekly SEO) ─────────────────────────────────────────────
export interface ReportSummary {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface ReportDeltas {
  clicks_pct: number;
  impressions_pct: number;
  ctr_pp: number;
  position_delta: number;
  prev_week: string;
}

export interface ReportAction {
  type: "cannibalization" | "content" | "new_article" | string;
  priority: number;
  title: string;
  description: string;
  target: string;
}

export interface ReportNextTopic {
  keyword: string;
  category: string;
  reason: string;
}

export interface ReportTopPage {
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface Report {
  week: string;
  generated_at: string;
  summary: ReportSummary;
  deltas: ReportDeltas;
  top_pages: ReportTopPage[];
  actions: ReportAction[];
  suggested_next_topic: ReportNextTopic | null;
  executive_summary: string;
}

export interface ReportIndexEntry {
  week: string;
  generated_at: string;
  summary: ReportSummary;
  deltas: ReportDeltas;
  action_count: number;
  has_next_topic: boolean;
}

export interface ReportsIndex {
  weeks: ReportIndexEntry[];
  total_weeks: number;
  cached_at: number;
  stale?: boolean;
}

// ─── Agents (Fase 3.5 · Fase 4) ───────────────────────────────────────
export type AgentMode = "seo_fixer" | "content_autopilot" | "plan";
export type AgentId = "seo" | "social";
export type AgentRunStatus = "queued" | "running" | "success" | "failure" | "cancelled";
export type AgentOutcome = "changed" | "no_changes" | "dispatched" | "error";

export interface AgentRunAction {
  index: number;
  type: string;
  priority: number;
  title: string;
  status: "applied" | "partial" | "skipped";
  note: string;
}

export interface AgentRunEdit {
  path: string;
  kind: "meta" | "canonical" | "robots" | "index" | "body";
  field: string;
  before: string;
  after: string;
}

export interface AgentGuard {
  ok: boolean;
  auto_merge: boolean;
  changed_files: string[];
  blockers: string[];
  review_reasons: string[];
}

export interface AgentRun {
  id: string;
  run_id: string;
  /** Which agent produced the run. Rows written before the field existed are
   *  backfilled to "seo". */
  agent: AgentId;
  /** SEO dispatch mode. Empty/null on a social run — it has no equivalent. */
  mode: AgentMode | "" | null;
  week: string;
  status: AgentRunStatus;
  agent_status: AgentOutcome | "";
  summary: string;
  actions_json: AgentRunAction[];
  edits_json: AgentRunEdit[];
  guard_json: AgentGuard | Record<string, never>;
  auto_merge: boolean;
  pr_url: string;
  run_url: string;
  requested_by: string;
  started_at: string;
  finished_at: string;
  created: string;
}

export interface AgentRunPayload {
  mode: AgentMode;
  week?: string;
  priorities?: string;
}

// ─── Agent registry + grid (Fase 4 · Entregable 2) ────────────────────
export interface AgentRegistryMode {
  id: string;
  label: string;
  blurb: string;
}

export interface AgentRegistryEntry {
  id: AgentId;
  name: string;
  does: string;
  workflow: string;
  /** Empty = the console cannot launch this agent (it runs on its own
   *  trigger). The detail page reads its launcher tiles from here. */
  modes: AgentRegistryMode[];
  runs_collection: string;
  detail_path: string;
}

export interface AgentGridCard {
  id: AgentId;
  name: string;
  does: string;
  detail_path: string;
  launchable: boolean;
  available: boolean;
  error?: string;
  latest: Partial<AgentRun> | null;
  active: number;
  recent_runs: number;
  /** null until agent_runs carries a per-run cost. */
  cost_usd: number | null;
}

export interface AgentGridResponse {
  cards: AgentGridCard[];
  fetched_at: string;
}

// ─── Summary (home dashboard) ─────────────────────────────────────────
interface CardBase {
  available: boolean;
  error?: string;
}

export interface SummaryResponse {
  analytics: CardBase & {
    period?: string;
    clicks?: number;
    impressions?: number;
    ctr?: number;
    position?: number;
    delta?: { clicks: number; impressions: number; ctr_pp: number; position: number };
    stale?: boolean;
  };
  blog: CardBase & {
    total_posts?: number;
    posts_with_perf?: number;
    total_clicks?: number;
    by_category?: Record<string, number>;
    latest?: { slug: string; title: string; published_at: string } | null;
  };
  reports: CardBase & {
    total_weeks?: number;
    latest_week?: string;
    generated_at?: string;
    action_count?: number;
    has_next_topic?: boolean;
  };
  approvals: CardBase & {
    pending?: number;
    failed?: number;
    oldest?: string | null;
    items?: Array<{ id: string; slug: string; title_es: string; status: string }>;
  };
  agents: CardBase & {
    total_runs?: number;
    active?: number;
    latest?: Partial<AgentRun> | null;
  };
  fetched_at: string;
}

// ─── marketing ────────────────────────────────────────────────────────────
// El agente social decide por plataforma. `saltado` es el caso que no cuesta
// nada: el triage descartó la red antes de escribir. `score` y `fit` son null
// en las corridas viejas, que no los tenían.
export type MarketingDecision = "publicar" | "suplente" | "rechazar" | "saltado";

export interface MarketingPlatform {
  platform: string;
  /** Cómo se publica: carrusel, texto, hilo, reel… Null en los archivos que
   *  preceden al modelo de red + formato. */
  formato: string | null;
  decision: MarketingDecision;
  score: number | null;
  fit: number | null;
  fit_why: string;
  attempts: number;
  best_of: (number | null)[];
  reason: string;
  slot: string;
  needs_production: boolean;
  has_content: boolean;
}

export interface MarketingPost {
  slug: string;
  title: string;
  url: string;
  category: string;
  week: string | null;
  generated_at: string;
  model: string;
  target: number | null;
  floor: number | null;
  cap: number | null;
  usage: { input_tokens: number; output_tokens: number };
  counts: Record<string, number>;
  platforms: MarketingPlatform[];
}

export interface MarketingSlot {
  platform: string;
  occupants: { slug: string; score: number | null; fit: number | null; claimed_at: string }[];
}

export interface MarketingResponse {
  generated: boolean;
  posts: MarketingPost[];
  week: { week: string | null; slots: MarketingSlot[]; known_weeks: string[] };
  summary: {
    posts: number;
    decisions: Record<string, number>;
    usage: { input_tokens: number; output_tokens: number };
    estimated_usd: number;
  };
}

/** La decisión del triage: existe antes que cualquier contenido. */
export interface MarketingTriage {
  slug: string;
  title: string;
  url: string;
  week: string;
  generated_at: string;
  /** Ya viene ordenado: primero las que van, por fit descendente. */
  redes: {
    red: string;
    fit: number;
    formato: string;
    generar: boolean;
    why: string;
    reason: string;
  }[];
  /** Cuántas redes generaría al aprobar. Cero = no hay nada que aprobar. */
  generarian: number;
}

export interface MarketingContent {
  slug: string;
  platform: string;
  decision: MarketingDecision;
  score: number | null;
  content: Record<string, unknown> | null;
  attempts: {
    attempt: number;
    score: number;
    passed_limits: boolean;
    verdict: string;
    factors: { factor: string; points: number; why: string }[];
    gaps: { problem: string; fix: string }[];
  }[];
  /** Carrusel, texto, hilo, reel… Null en los archivos previos al modelo de
   *  red + formato. Es lo que decide si hay algo renderizado que mirar. */
  formato: string | null;
  needs_production: boolean;
}

/** Un carrusel renderizado, listo para mirar y decidir.
 *
 *  Las slides llegan como data URI y no como URL: el repo es privado y un
 *  `<img src>` no manda el token. Son previews de 620px — lo que se sube a la
 *  red es el PNG de 1080x1350, que viaja como artefacto del workflow. */
export interface MarketingCarousel {
  slug: string;
  /** Dirección visual con la que se renderizó: azul, cromo, ácido… */
  style: string;
  slides: { n: number; name: string; src: string }[];
  bytes: number;
  /** Null mientras nadie decidió. Los dos estados son finales. */
  review: {
    status: "approved" | "rejected";
    decided_at: string | null;
    decided_by: string | null;
    red: string | null;
    style: string | null;
  } | null;
}

// ─── Briefing diario (ClickUp) ────────────────────────────────────────
export interface DailyTask {
  id: string;
  name: string;
  url: string;
  status: string | null;
  priority: "urgent" | "high" | "normal" | "low" | null;
  hours: number;
  due: string | null;
  list: string | null;
  folder: string | null;
  energy: "Deep" | "Light" | "Admin" | null;
  impact: string | null;
  waiting_on: string | null;
  /** "D1".."D4", parseado de la descripción que escribe Andrés. */
  block_type: string | null;
  /** Cuántas tareas se destraban al cerrar ésta. Manda sobre la prioridad. */
  unblocks?: number;
  score?: number;
  /** Si entra en la capacidad de hoy sumando las de arriba. */
  fits?: boolean;
  /** Nadie la tomó en ClickUp — sale en el tablero de todos. */
  unassigned?: boolean;
  blocked_by?: { id: string; name: string; url: string | null }[];
  owner?: string;
}

export interface DailyBriefing {
  available: boolean;
  error?: string;
  /** El briefing guardado no es de hoy — el job de las 7am no corrió. */
  stale?: boolean;
  day?: string;
  /** Correo del Console (PocketBase), no el de ClickUp. */
  user_email?: string;
  clickup_user_id?: string;
  generated_at?: string;
  /** null = ese usuario no tiene plan de horas. Distinto de 0 (descanso). */
  capacity_hours?: number | null;
  committed_hours?: number;
  overdue?: DailyTask[];
  focus?: DailyTask[];
  blocked?: DailyTask[];
  waiting_on_others?: DailyTask[];
  milestones?: DailyTask[];
  warnings?: string[];
  stats?: {
    open: number;
    overdue: number;
    actionable: number;
    blocked: number;
    waiting: number;
  };
}

export const api = {
  approve:    (id: string) => request(`/approvals/${id}/approve`,    { method: "POST" }),
  regenerate: (id: string) => request(`/approvals/${id}/regenerate`, { method: "POST" }),
  reject:     (id: string) => request(`/approvals/${id}/reject`,     { method: "POST" }),
  health:     () => request<{ status: string; checks: Record<string, string> }>("/health"),

  analytics: {
    read: (refresh = false) =>
      request<AnalyticsResponse>(`/analytics${refresh ? "?refresh=true" : ""}`),
  },

  blog: {
    read: (refresh = false) =>
      request<BlogResponse>(`/blog${refresh ? "?refresh=true" : ""}`),
  },

  summary: {
    read: () => request<SummaryResponse>("/summary"),
  },

  daily: {
    read: () => request<DailyBriefing>("/daily"),
    // Rehacerlo a mano: el día cambia después de las 7am y un briefing viejo
    // manda a trabajar en algo que ya se cerró.
    refresh: () => request<DailyBriefing>("/daily/refresh", { method: "POST" }),
  },

  marketing: {
    read: (week?: string, refresh = false) => {
      const qs = new URLSearchParams();
      if (week) qs.set("week", week);
      if (refresh) qs.set("refresh", "true");
      const q = qs.toString();
      return request<MarketingResponse>(`/marketing${q ? `?${q}` : ""}`);
    },
    // El copy va aparte del índice: un carrusel completo son varios KB.
    content: (slug: string, platform: string) =>
      request<MarketingContent>(
        `/marketing/${encodeURIComponent(slug)}/${encodeURIComponent(platform)}`,
      ),
    // Los triages esperando aprobación: existen antes que cualquier contenido.
    pending: () =>
      request<{ pendientes: MarketingTriage[]; count: number }>("/marketing/pending"),
    // Lo que decidió el triage, antes de que exista contenido.
    triage: (slug: string) =>
      request<MarketingTriage>(`/marketing/${encodeURIComponent(slug)}/triage`),
    // Aprobar dispara la generación: hasta acá no se escribió nada. `redes`
    // acota a un subconjunto de las que el triage aprobó — cada una cuesta
    // una corrida completa, así que querer sólo una es lo normal.
    approve: (slug: string, redes?: string[]) =>
      request<{ ok: boolean; slug: string; redes: string[]; generarian: number }>(
        `/marketing/${encodeURIComponent(slug)}/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ redes: redes ?? null }),
        },
      ),
    // Las slides renderizadas. Pesan ~275 KB, así que se piden sólo al abrir.
    carousel: (slug: string) =>
      request<MarketingCarousel>(`/marketing/${encodeURIComponent(slug)}/carousel`),
    // Aprobar = esta versión está bien, queda lista para subir.
    // Rechazar = se cancela y muere ahí. No hay regeneración.
    decideCarousel: (slug: string, decision: "approve" | "reject") =>
      request<{ ok: boolean; slug: string; review: MarketingCarousel["review"] }>(
        `/marketing/${encodeURIComponent(slug)}/carousel/${decision}`,
        { method: "POST" },
      ),
  },

  agents: {
    runs: (limit = 30, agent?: AgentId) => {
      const qs = new URLSearchParams({ limit: String(limit) });
      if (agent) qs.set("agent", agent);
      return request<{ runs: AgentRun[]; count: number; agent: AgentId | null; fetched_at: string }>(
        `/agents/runs?${qs}`,
      );
    },
    // The declarative registry — names, workflows, and per-agent modes.
    registry: () => request<{ agents: AgentRegistryEntry[] }>("/agents/registry"),
    // One live card per registered agent, for the /agents landing grid.
    grid: () => request<AgentGridResponse>("/agents/grid"),
    run: (payload: AgentRunPayload) =>
      request<{ ok: boolean; dispatched: boolean; dry_run?: boolean }>("/agents/run", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    // Called right after a dispatch: repository_dispatch returns no run id, so
    // the row only appears once the backend has polled GitHub.
    sync: () => request<{ ok: boolean; synced: number }>("/agents/sync", { method: "POST" }),
  },

  reports: {
    list: (refresh = false) =>
      request<ReportsIndex>(`/reports${refresh ? "?refresh=true" : ""}`),
    get: (week: string, refresh = false) =>
      request<Report>(`/reports/${week}${refresh ? "?refresh=true" : ""}`),
  },

  gsc: {
    status:   () => request<GscStatus>("/gsc/status"),
    authStart: () => request<{ url: string }>("/gsc/auth"),
    sites:    () => request<{ sites: GscSite[] }>("/gsc/sites"),
    keywords: (params: { site: string; months?: number; strikingOnly?: boolean; forceRefresh?: boolean }) => {
      const qs = new URLSearchParams({
        site: params.site,
        months: String(params.months ?? 6),
        striking_only: String(params.strikingOnly ?? true),
        force_refresh: String(params.forceRefresh ?? false),
      });
      return request<GscKeywordsResponse>(`/gsc/keywords?${qs}`);
    },
    generate: (payload: GscGeneratePayload) =>
      request<{ ok: boolean; dispatched: boolean; payload: Record<string, unknown> }>(
        "/gsc/generate",
        { method: "POST", body: JSON.stringify(payload) },
      ),
  },
};
